/**
 * Place map markers component - THREE-TIER ZOOM SYSTEM
 *
 * TIER 1 - WORLD (zoom 0-7):
 *   Shows server-side clusters as purple circles with counts
 *   Click cluster → zoom to region view
 *
 * TIER 2 - REGION (zoom 8-12):
 *   Shows server-side clusters as purple circles
 *   User CAN toggle to see client-clustered individual places
 *   Click cluster → zoom to street view
 *
 * TIER 3 - STREET (zoom 13+):
 *   Shows individual place markers - NO CLUSTERING EVER
 *   Every pin is visible (up to 800)
 *   Click pin → show place details
 */

import { useMemo, memo, useEffect, useRef, useCallback } from 'react';
import { Marker, Tooltip } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import type { MapCluster, MapPlace, MapTier } from '@/types/place';

interface PlaceMapMarkersProps {
  /** Server-side clusters */
  clusters?: MapCluster[];
  /** Individual places */
  places?: MapPlace[];
  /** Whether data is server-clustered */
  clustered?: boolean;
  /** Current tier: world, region, or street */
  tier?: MapTier;
  /** Selected place ID for highlighting */
  selectedPlaceId?: string | null;
  /** Enable client-side clustering (only applies in region tier) */
  clusteringEnabled?: boolean;
  /** Client-side cluster radius in pixels (only applies in region tier) */
  clusterRadius?: number;
  /** Callback when a place is clicked */
  onPlaceClick?: (place: MapPlace) => void;
  /** Callback when a cluster is clicked (to zoom in) */
  onClusterClick?: (cluster: MapCluster) => void;
}

// Colors
const MARKER_COLOR = '#722ed1';
const SELECTED_COLOR = '#1677ff';
const CLUSTER_COLOR = '#722ed1';

// CSS for markers
const MARKER_CSS = `
.place-marker-icon {
  width: 24px;
  height: 24px;
  background: ${MARKER_COLOR};
  border: 2px solid white;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0,0,0,0.3);
  display: flex;
  align-items: center;
  justify-content: center;
}
.place-marker-icon.selected {
  background: ${SELECTED_COLOR};
  width: 28px;
  height: 28px;
}
.place-marker-icon svg {
  width: 14px;
  height: 14px;
  fill: white;
}
.server-cluster-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${CLUSTER_COLOR};
  border: 2px solid white;
  border-radius: 50%;
  box-shadow: 0 2px 6px rgba(0,0,0,0.35);
  color: white;
  font-weight: 700;
  font-size: 11px;
  cursor: pointer;
}
.server-cluster-icon:hover {
  transform: scale(1.1);
  box-shadow: 0 3px 8px rgba(0,0,0,0.4);
}
.server-cluster-tooltip {
  text-align: center;
  font-size: 12px;
}
.server-cluster-tooltip-count {
  font-size: 16px;
  font-weight: 700;
  color: ${CLUSTER_COLOR};
}
.server-cluster-tooltip-label {
  font-size: 11px;
  color: #666;
}
.place-tooltip {
  min-width: 140px;
  max-width: 220px;
}
.place-tooltip-name {
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 2px;
  line-height: 1.3;
}
.place-tooltip-category {
  display: inline-block;
  background: #f3e8ff;
  color: #722ed1;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 10px;
  margin-bottom: 4px;
}
.place-tooltip-rating {
  font-size: 11px;
  color: #faad14;
}
`;

// Inject CSS once
let cssInjected = false;
function injectCSS() {
  if (cssInjected || typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.textContent = MARKER_CSS;
  document.head.appendChild(style);
  cssInjected = true;
}

// Create marker icon
const createMarkerIcon = (isSelected: boolean) =>
  L.divIcon({
    className: '',
    html: `
      <div class="place-marker-icon ${isSelected ? 'selected' : ''}">
        <svg viewBox="0 0 24 24">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
        </svg>
      </div>
    `,
    iconSize: isSelected ? [28, 28] : [24, 24],
    iconAnchor: isSelected ? [14, 28] : [12, 24],
  });

// Static icons
const defaultIcon = createMarkerIcon(false);
const selectedIcon = createMarkerIcon(true);

/**
 * Calculate server cluster circle size based on count (diameter in pixels)
 */
function getClusterSize(count: number): number {
  if (count < 10) return 28;
  if (count < 50) return 34;
  if (count < 100) return 40;
  if (count < 500) return 48;
  if (count < 1000) return 56;
  return 64;
}

/**
 * Format cluster count for display
 */
function formatCount(count: number): string {
  if (count < 1000) return count.toString();
  if (count < 10000) return (count / 1000).toFixed(1) + 'k';
  return Math.floor(count / 1000) + 'k';
}

/**
 * Create cluster icon with count displayed inside
 */
function createClusterIcon(count: number): L.DivIcon {
  const size = getClusterSize(count);
  const fontSize = size < 40 ? 10 : size < 50 ? 11 : 12;

  return L.divIcon({
    className: '',
    html: `<div class="server-cluster-icon" style="width:${size}px;height:${size}px;font-size:${fontSize}px;">${formatCount(count)}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

/**
 * Server-side cluster marker (purple circle with count displayed inside)
 */
const ServerClusterMarker = memo(function ServerClusterMarker({
  cluster,
  onClick,
}: {
  cluster: MapCluster;
  onClick?: () => void;
}) {
  const icon = createClusterIcon(cluster.count);
  const size = getClusterSize(cluster.count);

  return (
    <Marker
      position={[cluster.lat, cluster.lng]}
      icon={icon}
      eventHandlers={{ click: onClick }}
    >
      <Tooltip direction="top" offset={[0, -size / 2]}>
        <div className="server-cluster-tooltip">
          <div className="server-cluster-tooltip-count">{cluster.count.toLocaleString()}</div>
          <div className="server-cluster-tooltip-label">places</div>
          {cluster.topCategory && (
            <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
              {cluster.topCategory}
            </div>
          )}
          {cluster.avgRating != null && (
            <div style={{ fontSize: 10, color: '#faad14', marginTop: 2 }}>
              ★ {cluster.avgRating.toFixed(1)} avg
            </div>
          )}
        </div>
      </Tooltip>
    </Marker>
  );
});

/**
 * Individual place marker with hover tooltip
 * Click opens the full details modal (tooltip closes automatically)
 */
const PlaceMarker = memo(function PlaceMarker({
  place,
  isSelected,
  onClick,
}: {
  place: MapPlace;
  isSelected: boolean;
  onClick: () => void;
}) {
  const markerRef = useRef<L.Marker>(null);
  const stars = place.rating
    ? '★'.repeat(Math.round(place.rating)) + '☆'.repeat(5 - Math.round(place.rating))
    : '';

  // On click: close tooltip, then fire the modal callback
  const handleClick = useCallback(() => {
    markerRef.current?.closeTooltip();
    onClick();
  }, [onClick]);

  return (
    <Marker
      ref={markerRef}
      position={[place.latitude, place.longitude]}
      icon={isSelected ? selectedIcon : defaultIcon}
      eventHandlers={{ click: handleClick }}
    >
      <Tooltip direction="top" offset={[0, -20]}>
        <div className="place-tooltip">
          <div className="place-tooltip-name">{place.name}</div>
          {place.category && (
            <span className="place-tooltip-category">{place.category}</span>
          )}
          {place.rating != null && (
            <div className="place-tooltip-rating">
              {stars} <span style={{ color: '#666', fontSize: 10 }}>({place.reviewCount || 0})</span>
            </div>
          )}
        </div>
      </Tooltip>
    </Marker>
  );
});

/**
 * Main component - THREE-TIER rendering logic
 */
export function PlaceMapMarkers({
  clusters = [],
  places = [],
  clustered = true,
  tier = 'world',
  selectedPlaceId,
  clusteringEnabled = true,
  clusterRadius = 50,
  onPlaceClick,
  onClusterClick,
}: PlaceMapMarkersProps) {
  // Inject CSS on mount
  useEffect(() => {
    injectCSS();
  }, []);

  // Server-side cluster markers (for world and region tiers when clustered)
  const serverClusterMarkers = useMemo(() => {
    if (!clustered || clusters.length === 0) return null;

    return clusters.map((cluster, index) => (
      <ServerClusterMarker
        key={`cluster-${index}-${cluster.lat.toFixed(4)}-${cluster.lng.toFixed(4)}`}
        cluster={cluster}
        onClick={() => onClusterClick?.(cluster)}
      />
    ));
  }, [clusters, clustered, onClusterClick]);

  // Individual place markers
  const placeMarkers = useMemo(() => {
    if (places.length === 0) return null;

    return places.map((place) => (
      <PlaceMarker
        key={place.placeId || `place-${place.id}`}
        place={place}
        isSelected={place.placeId === selectedPlaceId}
        onClick={() => onPlaceClick?.(place)}
      />
    ));
  }, [places, selectedPlaceId, onPlaceClick]);

  // ═══════════════════════════════════════════════════════════════
  // TIER 3: STREET VIEW (zoom 13+)
  // ALWAYS show individual markers - NO clustering whatsoever
  // ═══════════════════════════════════════════════════════════════
  if (tier === 'street') {
    return <>{placeMarkers}</>;
  }

  // ═══════════════════════════════════════════════════════════════
  // TIER 1: WORLD VIEW (zoom 0-7)
  // ALWAYS show server clusters - no toggle available
  // ═══════════════════════════════════════════════════════════════
  if (tier === 'world') {
    return <>{serverClusterMarkers}</>;
  }

  // ═══════════════════════════════════════════════════════════════
  // TIER 2: REGION VIEW (zoom 8-12)
  // Show server clusters, OR client-clustered places if user toggled
  // ═══════════════════════════════════════════════════════════════

  // If server returned clusters, show them
  if (clustered) {
    return <>{serverClusterMarkers}</>;
  }

  // Server returned individual places - apply client-side clustering if enabled
  if (clusteringEnabled && places.length > 30) {
    return (
      <MarkerClusterGroup
        chunkedLoading
        maxClusterRadius={clusterRadius}
        spiderfyOnMaxZoom
        showCoverageOnHover={false}
        animate
        disableClusteringAtZoom={13}
      >
        {placeMarkers}
      </MarkerClusterGroup>
    );
  }

  // Client-side clustering disabled - show all markers directly
  return <>{placeMarkers}</>;
}

export default PlaceMapMarkers;
